/**
 * RolloutSecretsSection: the environment values a team's agent needs when
 * TrainLoop runs it (TRA-1215). The nightly evals and training runs put
 * them in the container as env, by name. Values are write-only (encrypted
 * at rest, never returned), so the page lists names and the form sends
 * only what changed: names to set and names to drop, in one save. Under
 * staff impersonation the form renders disabled; the API refuses the write.
 */

import { useState, type FormEvent } from 'react'
import { Plus, Trash2, Undo2 } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { Button } from '@/components/ui/button'
import { EYEBROW } from '@/components/ui/eyebrow'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import { useRolloutSecrets, useUpdateRolloutSecrets } from '@/hooks/useTeamSettings'

import { RowAction } from './RowAction'
import { SettingsFormFooter } from './SettingsFormFooter'
import { SettingsList, SettingsListRow } from './SettingsList'
import { SettingsPageHeader } from './SettingsPageHeader'
import { SettingsBlock, SettingsSection } from './SettingsSection'

export function RolloutSecretsSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { impersonatingTeamId } = useTeam()
  const secrets = useRolloutSecrets()
  return (
    <div>
      <SettingsPageHeader section={section} />
      {secrets.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : secrets.isError ? (
        <>
          <SettingsBlock>
            <p className="text-sm text-destructive" role="alert">
              {getApiErrorDetail(secrets.error) ?? 'Failed to load rollout secrets.'}
            </p>
          </SettingsBlock>
          {/* A 503 is a store that cannot be read (the api says to re-enter
              the values), and a save replaces it, so the form stays. */}
          {getApiErrorStatus(secrets.error) === 503 && (
            <RolloutSecretsForm names={[]} readOnly={impersonatingTeamId !== null} />
          )}
        </>
      ) : secrets.data ? (
        <RolloutSecretsForm
          names={secrets.data.configured_names}
          readOnly={impersonatingTeamId !== null}
        />
      ) : null}
    </div>
  )
}

/** One row of the add-or-replace list; `id` keeps React keys stable while rows come and go. */
interface Entry {
  id: number
  name: string
  value: string
}

/** Mirrors the API's EnvironmentName shape; reserved names are its 422. */
const NAME_REGEX = /^[A-Z][A-Z0-9_]*$/

/** The first thing wrong with the edits, or null when they can be saved. */
function validateEdits(entries: Entry[], removed: string[]): string | null {
  const names = entries.map((entry) => entry.name.trim())
  if (names.some((name) => !NAME_REGEX.test(name))) {
    return 'A name is uppercase letters, digits and underscores, and starts with a letter.'
  }
  if (entries.some((entry) => entry.value === '')) return 'Every name needs a value.'
  if (new Set(names).size !== names.length) return 'Each name can be entered once.'
  const clash = names.find((name) => removed.includes(name))
  if (clash !== undefined) return `${clash} cannot be set and removed in one save.`
  return null
}

function RolloutSecretsForm({
  names,
  readOnly,
}: {
  names: string[]
  readOnly: boolean
}): React.JSX.Element {
  const saveMutation = useUpdateRolloutSecrets()
  const [removed, setRemoved] = useState<string[]>([])
  const [entries, setEntries] = useState<Entry[]>([{ id: 0, name: '', value: '' }])
  const [nextId, setNextId] = useState(1)
  // The API's answer to the last save. Any edit retires it.
  const [saveError, setSaveError] = useState<string | null>(null)

  const filled = entries.filter((entry) => entry.name.trim() !== '' || entry.value !== '')
  const dirty = removed.length > 0 || filled.length > 0
  const validationError = dirty ? validateEdits(filled, removed) : null
  const canSubmit = !readOnly && !saveMutation.isPending && dirty && validationError === null

  const reset = (): void => {
    setRemoved([])
    setEntries([{ id: nextId, name: '', value: '' }])
    setNextId((id) => id + 1)
    setSaveError(null)
  }
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    setSaveError(null)
    saveMutation.mutate(
      {
        data: {
          rollout_env: Object.fromEntries(filled.map((entry) => [entry.name.trim(), entry.value])),
          remove: removed,
        },
      },
      {
        onSuccess: () => {
          reset()
          toast.success('Rollout secrets saved')
        },
        onError: (error) => {
          const detail = getApiErrorDetail(error) ?? 'Failed to save rollout secrets'
          const status = getApiErrorStatus(error)
          if (status === 400 || status === 422) setSaveError(detail)
          else toast.error(detail)
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* One disabled fieldset turns every control off under impersonation. */}
      <fieldset disabled={readOnly} className="min-w-0">
        <SettingsSection
          title="Stored names"
          description="Values are encrypted and never shown. Remove a name here, or replace its value below."
        >
          <StoredNames
            names={names}
            removed={removed}
            onToggle={(name, remove) => {
              setRemoved((previous) =>
                remove ? [...previous, name] : previous.filter((other) => other !== name),
              )
              setSaveError(null)
            }}
          />
        </SettingsSection>

        <SettingsSection
          title="Add or replace"
          description="Enter a name and its value. A name that is already stored gets the new value."
        >
          <EntryRows
            entries={entries}
            stored={names}
            onChange={(next) => {
              setEntries(next)
              setSaveError(null)
            }}
            onAdd={() => {
              setEntries((previous) => [...previous, { id: nextId, name: '', value: '' }])
              setNextId((id) => id + 1)
            }}
          />
        </SettingsSection>

        <SettingsFormFooter dirty={dirty} error={validationError ?? saveError}>
          <Button type="button" variant="outline" disabled={!dirty || saveMutation.isPending} onClick={reset}>
            Discard
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Save
          </Button>
        </SettingsFormFooter>
      </fieldset>
    </form>
  )
}

function StoredNames({
  names,
  removed,
  onToggle,
}: {
  names: string[]
  removed: string[]
  onToggle: (name: string, remove: boolean) => void
}): React.JSX.Element {
  if (names.length === 0) {
    return <p className="text-sm text-muted-foreground">No values are stored yet.</p>
  }
  return (
    <SettingsList>
      {names.map((name) => {
        const pending = removed.includes(name)
        return (
          <SettingsListRow key={name}>
            <span className="flex min-w-0 flex-1 items-center gap-3">
              <span className={pending ? 'font-mono text-sm text-muted-foreground line-through' : 'font-mono text-sm'}>
                {name}
              </span>
              {pending && <span className={EYEBROW}>Removed on save</span>}
            </span>
            {pending ? (
              <RowAction label={`Keep ${name}`} icon={Undo2} onClick={() => { onToggle(name, false) }} />
            ) : (
              <RowAction label={`Remove ${name}`} icon={Trash2} destructive onClick={() => { onToggle(name, true) }} />
            )}
          </SettingsListRow>
        )
      })}
    </SettingsList>
  )
}

function EntryRows({
  entries,
  stored,
  onChange,
  onAdd,
}: {
  entries: Entry[]
  stored: string[]
  onChange: (entries: Entry[]) => void
  onAdd: () => void
}): React.JSX.Element {
  const update = (id: number, patch: Partial<Entry>): void => {
    onChange(entries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)))
  }
  return (
    <div className="max-w-2xl space-y-3">
      {entries.map((entry, index) => (
        <EntryRow
          key={entry.id}
          entry={entry}
          index={index}
          replaces={stored.includes(entry.name.trim())}
          removable={entries.length > 1}
          onChange={(patch) => { update(entry.id, patch) }}
          onRemove={() => { onChange(entries.filter((other) => other.id !== entry.id)) }}
        />
      ))}
      <Button type="button" variant="outline" size="sm" onClick={onAdd}>
        <Plus />
        Add another
      </Button>
    </div>
  )
}

function EntryRow({
  entry,
  index,
  replaces,
  removable,
  onChange,
  onRemove,
}: {
  entry: Entry
  index: number
  replaces: boolean
  removable: boolean
  onChange: (patch: Partial<Entry>) => void
  onRemove: () => void
}): React.JSX.Element {
  const nameId = `rollout-secret-name-${index.toString()}`
  const valueId = `rollout-secret-value-${index.toString()}`
  return (
    <div className="space-y-1">
      <div className="flex items-start gap-2">
        <div className="w-2/5 space-y-1">
          <Label htmlFor={nameId} className="sr-only">
            Name
          </Label>
          <Input
            id={nameId}
            className="font-mono"
            value={entry.name}
            autoComplete="off"
            spellCheck={false}
            placeholder="GCP_PROJECT_ID"
            onChange={(e) => { onChange({ name: e.target.value.toUpperCase() }) }}
          />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <Label htmlFor={valueId} className="sr-only">
            Value
          </Label>
          <Input
            id={valueId}
            className="font-mono"
            value={entry.value}
            autoComplete="off"
            spellCheck={false}
            placeholder="Value"
            onChange={(e) => { onChange({ value: e.target.value }) }}
          />
        </div>
        <RowAction label="Drop this row" icon={Trash2} disabled={!removable} onClick={onRemove} />
      </div>
      {replaces && <p className="text-xs text-muted-foreground">This replaces the stored value.</p>}
    </div>
  )
}
